import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { TaskDrawer } from '../components/TaskDrawer';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import type { IssueList, IssueRow } from '../lib/types';
import { SLA_STANDING_LABELS, WORK_ITEM_TYPE_GLYPH, WORK_ITEM_TYPE_LABELS } from '../lib/types';
import { useSession } from '../lib/session';
import { WorkFilters } from '../components/WorkFilters';
import { AssigneeStack } from '../components/Assignees';
import { SlaBadge } from '../components/SlaBadge';
import { BillingBadge, PriorityBadge } from '../components/Badges';
import type { BoardFilter } from '../lib/boardFilter';
import { EMPTY_FILTER, dedupe, isFiltering, matches } from '../lib/boardFilter';
import { formatDate, todayValue } from '../lib/dates';
import { deadlineKey, timeLeft, urgencyKey } from '../lib/timeLeft';
import type { WorkSource } from '../lib/workSource';
import { WORK_SOURCES, WORK_SOURCE_HINTS, WORK_SOURCE_LABELS, sourceOf } from '../lib/workSource';

/** A row with the company it belongs to, which a list spanning companies has to say. */
type WorkRow = IssueRow & { company: { id: string; name: string; code: string } };

type Lane = WorkSource | 'all';

const FILTER_KEY = 'hub.workFilter';

/**
 * ALL THE WORK, the way it reaches us — one page in place of Priorities and Issues.
 *
 * Those two were the same question asked twice: Priorities a short ranked list for one
 * company, Issues the full list for one company. Neither asked the question that decides what
 * to pick up, which is WHERE THE WORK CAME FROM. A change request is a project of its own, from
 * its document through testing to live; everything else is a task, a bug or a piece of design,
 * and lives in that company's Tasks, Bugs or Graphic Design project. The lanes across the top
 * split by exactly that, and the list under them spans every company by default.
 *
 * In order of what to do first: priority, then whichever has the least time left — the due
 * date or the SLA clock, whichever is nearer (see `lib/timeLeft`).
 */
export function WorkPage() {
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const companies = session?.companies ?? [];

  const companyId = params.get('company') ?? 'all';
  const lane = (params.get('lane') as Lane | null) ?? 'all';

  const [lists, setLists] = useState<IssueList[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const [filter, setFilter] = useState<BoardFilter>(() => {
    try {
      const saved = localStorage.getItem(FILTER_KEY);
      return saved ? { ...EMPTY_FILTER, ...JSON.parse(saved) } : EMPTY_FILTER;
    } catch {
      return EMPTY_FILTER;
    }
  });
  const [open, setOpen] = useState<{ id: string; projectId: string } | null>(null);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  /**
   * One request per company, side by side. Each company's list already applies who may see
   * what, so asking each of them is what keeps this page from having a visibility rule of its
   * own to get wrong.
   */
  const scope = useMemo(
    () => (companyId === 'all' ? companies : companies.filter((company) => company.id === companyId)),
    [companies, companyId],
  );
  const scopeKey = scope.map((company) => company.id).join(',');

  const load = useCallback(async () => {
    try {
      const query = showClosed ? '?includeClosed=true' : '';
      const answered = await Promise.all(
        scope.map((company) => api.get<IssueList>(`/companies/${company.id}/issues${query}`).catch(() => null)),
      );
      setLists(answered.filter((list): list is IssueList => list !== null && !list.withheld));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the work');
    }
    // `scopeKey` stands for `scope`, whose identity changes on every session read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, showClosed]);

  useEffect(() => {
    void load();
  }, [load]);

  function changeFilter(next: BoardFilter) {
    setFilter(next);
    try {
      if (isFiltering(next)) localStorage.setItem(FILTER_KEY, JSON.stringify(next));
      else localStorage.removeItem(FILTER_KEY);
    } catch {
      /* private browsing */
    }
  }

  const meId = session?.person.id ?? null;
  const today = todayValue();

  const all: WorkRow[] = useMemo(
    () =>
      (lists ?? []).flatMap((list) =>
        list.items.map((item) => ({ ...item, company: { id: list.company.id, name: list.company.name, code: list.company.code } })),
      ),
    [lists],
  );

  /** What the filters leave, before the lane — so each lane's count answers "how many here". */
  const filtered = useMemo(() => all.filter((item) => matches(item, filter, meId)), [all, filter, meId]);
  const counts = useMemo(() => {
    const out: Record<Lane, number> = { all: filtered.length, change_request: 0, tasks: 0, bugs: 0, design: 0 };
    for (const item of filtered) out[sourceOf(item)] += 1;
    return out;
  }, [filtered]);
  const rows = useMemo(
    () => (lane === 'all' ? filtered : filtered.filter((item) => sourceOf(item) === lane)),
    [filtered, lane],
  );

  const facets = useMemo(
    () => ({
      people: dedupe(
        all.flatMap((item) => item.assignees),
        (person) => person.id,
      ).sort((a, b) => a.name.localeCompare(b.name)),
      categories: dedupe(
        all.flatMap((item) => (item.category ? [item.category] : [])),
        (category) => category.id,
      )
        .map((category) => ({ id: category.id, path: category.path }))
        .sort((a, b) => a.path.localeCompare(b.path)),
      projects: (lists ?? [])
        .flatMap((list) =>
          list.projects.map((project) => ({
            ...project,
            name: companyId === 'all' && (lists?.length ?? 0) > 1 ? `${list.company.code} · ${project.name}` : project.name,
          })),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    }),
    [all, lists, companyId],
  );

  const manyCompanies = (lists?.length ?? 0) > 1;

  const columns: Column<WorkRow>[] = useMemo(
    () => [
      {
        key: 'key',
        header: 'Item',
        width: '120px',
        value: (item) => item.key,
        filter: 'text',
        render: (item) => <span className="key">{item.key}</span>,
      },
      {
        key: 'title',
        header: 'Title',
        width: '30%',
        value: (item) => item.title,
        filter: 'text',
        render: (item) => (
          <span className="row" style={{ gap: 6 }}>
            <span>{item.title}</span>
            {item.blockedSince && <span className="badge badge-accent">blocked</span>}
            {item.reopenCount > 0 && <span className="badge badge-warn">↻{item.reopenCount}</span>}
            {item.closedAt && <span className="badge">closed</span>}
            <BillingBadge exception={item.billingException} />
          </span>
        ),
      },
      {
        key: 'source',
        header: 'Came in as',
        value: (item) => WORK_SOURCE_LABELS[sourceOf(item)],
        filter: 'choice',
        render: (item) => <span className="muted">{WORK_SOURCE_LABELS[sourceOf(item)]}</span>,
      },
      {
        key: 'company',
        header: 'Company',
        hiddenByDefault: !manyCompanies,
        value: (item) => item.company.name,
        filter: 'choice',
        render: (item) => (
          <Link to={`/companies/${item.company.id}`} onClick={(event) => event.stopPropagation()} className="muted">
            {item.company.name}
          </Link>
        ),
      },
      {
        key: 'project',
        header: 'Project',
        value: (item) => item.project.name,
        filter: 'choice',
        render: (item) => (
          <Link to={`/projects/${item.project.id}/board`} onClick={(event) => event.stopPropagation()} className="muted">
            {item.project.name}
          </Link>
        ),
      },
      {
        key: 'status',
        header: 'Stage',
        value: (item) => item.status?.label ?? '',
        filter: 'choice',
        render: (item) =>
          item.status ? (
            <span className="status-pill" style={{ background: item.status.color }}>
              {item.status.label}
            </span>
          ) : (
            <span className="dim">—</span>
          ),
      },
      {
        key: 'priority',
        header: 'Priority',
        value: (item) => (item.priority === 'none' ? '' : item.priority),
        /* Priority, then least time left — so the default order is "what to do first". */
        sortValue: (item) => urgencyKey(item.priority, timeLeft(item, today)),
        filter: 'choice',
        render: (item) => <PriorityBadge priority={item.priority} />,
      },
      {
        key: 'left',
        header: 'Time left',
        value: (item) => timeLeft(item, today).label,
        sortValue: (item) => deadlineKey(timeLeft(item, today)),
        render: (item) => {
          const left = timeLeft(item, today);
          if (!left.label) return <span className="dim">—</span>;
          return (
            <span
              className="mono"
              style={
                left.late
                  ? { color: 'var(--accent)', fontWeight: 600 }
                  : left.soon
                    ? { color: 'var(--warn)', fontWeight: 600 }
                    : undefined
              }
            >
              {left.label}
            </span>
          );
        },
      },
      {
        key: 'assigned',
        header: 'Assigned',
        value: (item) => item.assignees.map((person) => person.name).join(', '),
        filter: 'text',
        render: (item) => <AssigneeStack people={item.assignees} size="sm" />,
      },
      {
        key: 'type',
        header: 'Type',
        hiddenByDefault: true,
        value: (item) => WORK_ITEM_TYPE_LABELS[item.type],
        filter: 'choice',
        render: (item) => (
          <span className="muted">
            <span aria-hidden>{WORK_ITEM_TYPE_GLYPH[item.type]}</span> {WORK_ITEM_TYPE_LABELS[item.type]}
          </span>
        ),
      },
      {
        key: 'sla',
        header: 'SLA',
        hiddenByDefault: true,
        value: (item) => (item.sla ? SLA_STANDING_LABELS[item.sla.standing] : ''),
        filter: 'choice',
        render: (item) => (item.sla ? <SlaBadge sla={item.sla} /> : <span className="dim">—</span>),
      },
      {
        key: 'due',
        header: 'Due',
        hiddenByDefault: true,
        value: (item) => item.dueDate,
        filter: 'text',
        render: (item) => <span className="mono">{formatDate(item.dueDate)}</span>,
      },
      {
        key: 'raised',
        header: 'Raised',
        hiddenByDefault: true,
        value: (item) => item.createdAt.slice(0, 10),
        filter: 'text',
        render: (item) => <span className="mono">{formatDate(item.createdAt.slice(0, 10))}</span>,
      },
    ],
    [today, manyCompanies],
  );

  if (companies.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <h3>No company access</h3>
          <p>You have not been given access to a company yet.</p>
        </div>
      </div>
    );
  }

  const closedCount = (lists ?? []).reduce((sum, list) => sum + list.closedCount, 0);
  const truncated = (lists ?? []).some((list) => list.truncated);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Work</h1>
          <p className="subtitle">
            Everything open{companyId === 'all' ? ' across every company' : ''}, in the order to pick it up —
            priority first, then whatever has least time left.
            {truncated ? ' Showing the first 1000 per company.' : ''}
          </p>
        </div>
        <div className="row">
          {companies.length > 1 && (
            <label className="field" style={{ marginBottom: 0, minWidth: 200 }}>
              <span className="field-label">Company</span>
              <select value={companyId} onChange={(event) => setParam('company', event.target.value === 'all' ? null : event.target.value)}>
                <option value="all">All companies</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {closedCount > 0 || showClosed ? (
            <button
              type="button"
              className={showClosed ? 'btn btn-sm' : 'btn btn-quiet btn-sm'}
              onClick={() => setShowClosed(!showClosed)}
              title="Closed work is kept, just off the list"
            >
              {showClosed ? 'Hide closed' : `Show ${closedCount} closed`}
            </button>
          ) : null}
        </div>
      </div>

      {/*
        Where it came from, which decides how it is worked. Counts are after the filters
        below, so each tab says how much of what you are looking for is in it.
      */}
      <div className="lane-tabs" role="tablist" aria-label="Where the work came from">
        {(['all', ...WORK_SOURCES] as Lane[]).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={lane === option}
            className={lane === option ? 'lane-tab is-on' : 'lane-tab'}
            title={option === 'all' ? 'Every kind of work' : WORK_SOURCE_HINTS[option]}
            onClick={() => setParam('lane', option === 'all' ? null : option)}
          >
            {option === 'all' ? 'All work' : WORK_SOURCE_LABELS[option]}
            <span className="lane-count">{counts[option]}</span>
          </button>
        ))}
      </div>

      <WorkFilters
        facets={facets}
        filter={filter}
        onChange={changeFilter}
        shown={rows.length}
        total={all.length}
        meId={meId}
        placeholder="Find across all of it — key, title, wording"
      />

      {error ? (
        <div className="callout">{error}</div>
      ) : !lists ? (
        <div className="empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>{isFiltering(filter) || lane !== 'all' ? 'Nothing here matches' : 'Nothing outstanding'}</h3>
            <p>
              {isFiltering(filter)
                ? `All ${all.length} item${all.length === 1 ? '' : 's'} are hidden by the filters above.`
                : lane !== 'all'
                  ? `No ${WORK_SOURCE_LABELS[lane].toLowerCase()} open right now.`
                  : 'Every project is clear.'}
            </p>
            {isFiltering(filter) && (
              <button type="button" className="btn btn-primary" onClick={() => changeFilter({ ...EMPTY_FILTER })}>
                Clear the filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-pad">
          <DataTable
            tableKey="work"
            columns={columns}
            rows={rows}
            showSearch={false}
            defaultSort={{ key: 'priority', direction: 'asc' }}
            exportName="work"
            onRowClick={(item) => setOpen({ id: item.id, projectId: item.project.id })}
            pageState={{ filter, showClosed, lane, company: companyId }}
            onPageState={(value) => {
              const saved = value as
                | { filter?: BoardFilter; showClosed?: boolean; lane?: Lane; company?: string }
                | undefined;
              if (saved?.filter) changeFilter({ ...EMPTY_FILTER, ...saved.filter });
              if (typeof saved?.showClosed === 'boolean') setShowClosed(saved.showClosed);
              if (saved?.lane || saved?.company) {
                const next = new URLSearchParams(params);
                if (saved.lane && saved.lane !== 'all') next.set('lane', saved.lane);
                else next.delete('lane');
                if (saved.company && saved.company !== 'all') next.set('company', saved.company);
                else next.delete('company');
                setParams(next, { replace: true });
              }
            }}
          />
        </div>
      )}

      {open && (
        <TaskDrawer
          itemId={open.id}
          projectId={open.projectId}
          onClose={() => setOpen(null)}
          onChanged={load}
        />
      )}
    </>
  );
}
